/**
 * Database-related constants for the application
 * 
 * These constants are used throughout the database service implementation
 * to configure behavior, optimize performance, and ensure consistent data handling.
 */

// Database prefixes for different data types
export const INSIGHTS_DB_PREFIX = 'insights';
export const MEDICATION_DB_PREFIX = 'medication';
export const ADHERENCE_DB_PREFIX = 'adherence';
export const USER_PROFILE_DB_PREFIX = 'user_profile';
export const REMINDER_DB_PREFIX = 'reminder';
export const NOTIFICATION_DB_PREFIX = 'notification';

// Cache duration constants
export const SHORT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
export const MEDIUM_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
export const LONG_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Database consistency check intervals
export const DB_CONSISTENCY_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Log categories for the logging service
export type LogCategory = 'database' | 'cache' | 'sync' | 'conflict' | 'network' | 'performance';

// Performance-related constants
export const LOW_END_DEVICE_THRESHOLD = 30; // Performance score threshold for low-end devices
export const BATTERY_SAVER_MODE = false; // User preference for battery saving mode

/**
 * Operation priority levels for the performance optimizer
 * Determines which operations take precedence during resource constraints
 * Using numeric values for easier comparison (lower = higher priority)
 */
export enum OperationPriority {
  HIGH = 0,       // User-facing, immediate operations (UI actions)
  MEDIUM = 1,     // Background operations with moderate urgency
  LOW = 2,        // Maintenance or non-critical operations
  SYNC = 3        // Data synchronization operations
}

/**
 * Synchronization states for the sync manager
 * Represents the current status of database synchronization
 */
export enum SyncState {
  INACTIVE = 'inactive',    // No sync activity
  ACTIVE = 'active',        // Sync is currently active
  PAUSED = 'paused',        // Sync is temporarily paused
  ERROR = 'error',          // Sync encountered an error
  COMPLETED = 'completed'   // Initial sync has completed
}

/**
 * Database migration versions
 * Used to track schema evolution and manage data migrations
 */
export const DB_MIGRATION_VERSIONS = [1, 2, 3];

/**
 * Current database version
 * Used throughout the application to determine compatibility
 */
export const CURRENT_DB_VERSION = 3;

/**
 * Document type versions
 * Tracks the current schema version for each document type
 */
export const DOCUMENT_VERSIONS = {
  [MEDICATION_DB_PREFIX]: 3,
  [ADHERENCE_DB_PREFIX]: 2,
  [USER_PROFILE_DB_PREFIX]: 3,
  [INSIGHTS_DB_PREFIX]: 2,
  [REMINDER_DB_PREFIX]: 1,
  [NOTIFICATION_DB_PREFIX]: 1
};

/**
 * Consolidated database configuration
 * Used by DatabaseService and related components
 */
/**
 * Performance optimization settings
 * Used to adjust database operations based on device capabilities
 */
export const PERFORMANCE_SETTINGS = {
  // Performance thresholds (0-100 scale)
  HIGH_PERFORMANCE_THRESHOLD: 75,
  LOW_PERFORMANCE_THRESHOLD: 35,
  
  // Concurrent operations limits
  DEFAULT_CONCURRENT_OPERATIONS: 8,  // Default for high-end devices
  MEDIUM_DEVICE_CONCURRENT_OPERATIONS: 5,
  LOW_DEVICE_CONCURRENT_OPERATIONS: 3,
  
  // Batch size settings
  DEFAULT_BATCH_SIZE: 50,
  MIN_BATCH_SIZE: 10,
  
  // Batch size reduction factors for different device types
  MEDIUM_DEVICE_BATCH_REDUCE_FACTOR: 0.7,  // Reduce by 30%
  LOW_DEVICE_BATCH_REDUCE_FACTOR: 0.5      // Reduce by 50%
};

/**
 * Operation retry settings
 * Controls the retry behavior for database operations
 */
export const OPERATION_RETRY_SETTINGS = {
  // Maximum number of retry attempts
  MAX_RETRIES: 3,
  
  // Initial backoff delay in milliseconds
  INITIAL_BACKOFF_MS: 500,
  
  // Maximum backoff delay in milliseconds
  MAX_BACKOFF_MS: 30000, // 30 seconds
  
  // Operations that should never be retried
  NEVER_RETRY_OPERATIONS: [
    'bulkDelete', // Avoid retrying bulk deletes as they may have partially succeeded
    'dropDatabase' // Never retry database drops
  ]
};

export const DB_CONFIG = {
  // Database name and prefix
  DB_NAME: 'meduler_db',
  DB_PREFIX: 'meduler',
  
  // Collection prefixes
  PREFIXES: {
    INSIGHTS: INSIGHTS_DB_PREFIX,
    MEDICATION: MEDICATION_DB_PREFIX,
    ADHERENCE: ADHERENCE_DB_PREFIX,
    USER_PROFILE: USER_PROFILE_DB_PREFIX,
    REMINDER: REMINDER_DB_PREFIX,
    NOTIFICATION: NOTIFICATION_DB_PREFIX
  },
  
  // Cache durations
  CACHE: {
    SHORT: SHORT_CACHE_DURATION,
    MEDIUM: MEDIUM_CACHE_DURATION,
    LONG: LONG_CACHE_DURATION,
  },
  
  // Basic configuration
  COMPACTION_INTERVAL: DB_CONSISTENCY_CHECK_INTERVAL,
  MAX_RETRIES: 3,
  RETRY_DELAY: 300, // milliseconds
  
  // Sync configuration
  REMOTE_URL: '', // Default empty, should be set at runtime if sync is needed
  SYNC_RETRY_MAX: 10, // Maximum number of sync retry attempts
  SYNC_INITIAL_RETRY_DELAY: 5000, // 5 seconds initial retry delay
  SYNC_MAX_RETRY_DELAY: 300000, // 5 minutes maximum retry delay
  SYNC_OPTIONS: {
    live: true,
    retry: true,
    batch_size: 50,
    batches_limit: 10,
    heartbeat: 10000, // 10 seconds heartbeat for live sync
    timeout: 30000    // 30 seconds timeout for sync operations
  },
  SYNC_DIRECTION_OPTIONS: {
    BOTH: 'both',
    PUSH: 'push',
    PULL: 'pull'
  },
  
  // Performance optimization
  DEFAULT_BATCH_SIZE: 50, // Default batch size for bulk operations
  DEFAULT_BATCH_DELAY: 50, // Default delay between batches (ms)
  MAX_CONCURRENT_OPS: 4, // Default max concurrent operations
  
  // Mobile-first optimizations
  MOBILE: {
    REDUCED_BATCH_SIZE: 25, // Smaller batch size for lower-end devices
    BATTERY_SAVER_SYNC_INTERVAL: 15 * 60 * 1000, // 15 minutes in battery saver mode
    OFFLINE_QUEUE_SIZE_LIMIT: 1000, // Max operations to queue when offline
    LOW_BANDWIDTH_MODE: false, // Default off, can be toggled by user
    COMPRESSION_ENABLED: true, // Enable compression for large documents
    ATTACHMENT_SIZE_LIMIT: 5 * 1024 * 1024 // 5MB limit for attachments
  },
  
  // Indexes for optimizing queries
  INDEXES: [
    { name: 'by-medication-name', fields: ['data.name'] },
    { name: 'by-schedule-date', fields: ['data.date'] },
    { name: 'by-type-and-date', fields: ['data.type', 'data.date'] },
    { name: 'by-user-id', fields: ['data.userId'] }
  ],
  
  // Migration configuration
  MIGRATION: {
    VERSIONS: DB_MIGRATION_VERSIONS,
    AUTO_MIGRATE: true, // Automatically migrate on startup
    DOCUMENT_VERSIONS: DOCUMENT_VERSIONS
  }
}
